import React from 'react';
import type { ApprovalItem } from '../client';
import { WorkspaceApprovalCard } from '../workspaces/WorkspaceApprovalCard';
import { ApprovalCard } from './ApprovalCard';
import { ApprovalCardHeader } from './ApprovalCardHeader';
import { InviteApprovalCard } from './InviteApprovalCard';
import { NetworkEgressApprovalCard } from './NetworkEgressApprovalCard';
import { SkillImportApprovalCard } from './SkillImportApprovalCard';
import SecretApprovalCard from '../secrets/SecretApprovalCard';

/**
 * Single dispatch point for rendering an approval request. Every surface
 * that lists approvals goes through here so each approval type has ONE
 * template everywhere (workspace requests get the full resource editor,
 * everything else the generic card).
 */
export const ApprovalRequestView: React.FC<{
  approval: ApprovalItem;
  deciding?: boolean;
  headerless?: boolean;
  onDecide: (
    decision: 'approved' | 'rejected',
    note: string,
    inputs?: Record<string, unknown>,
  ) => Promise<void>;
}> = ({ approval, deciding, headerless, onDecide }) => {
  const token =
    approval.type === 'workspace_resource' && approval.detail
      ? String(approval.detail.token || '')
      : '';
  if (token) {
    return (
      <article
        className="approval-card approval-card-workspace"
        data-testid={`approval-${approval.id}`}>
        <ApprovalCardHeader
          teamId={approval.teamId}
          agent={approval.requestingAgent}
          requestingUser={approval.requestingUser}
          requestingTrigger={approval.requestingTrigger}
          origin={approval.origin}
          createdAt={approval.createdAt}
          title={approval.title}
        />
        <WorkspaceApprovalCard
          teamId={approval.teamId}
          token={token}
          headerless
        />
      </article>
    );
  }
  if (approval.type === 'invite_user') {
    return (
      <article
        className="approval-card approval-card-invite"
        data-testid={`approval-${approval.id}`}>
        <ApprovalCardHeader
          teamId={approval.teamId}
          agent={approval.requestingAgent}
          requestingUser={approval.requestingUser}
          requestingTrigger={approval.requestingTrigger}
          origin={approval.origin}
          createdAt={approval.createdAt}
          title={approval.title}
        />
        <InviteApprovalCard approvalId={approval.id} headerless />
      </article>
    );
  }
  if (approval.type === 'skill_import') {
    return (
      <SkillImportApprovalCard
        approval={approval}
        deciding={deciding}
        onDecide={onDecide}
      />
    );
  }
  if (
    approval.type === 'secret_access' ||
    approval.type === 'secret_mutation' ||
    approval.type === 'secret_create'
  ) {
    return (
      <SecretApprovalCard
        approval={approval}
        deciding={deciding}
        headerless={headerless}
        onDecide={onDecide}
      />
    );
  }
  if (approval.type === 'network_egress') {
    return (
      <NetworkEgressApprovalCard
        approval={approval}
        deciding={deciding}
        onDecide={onDecide}
      />
    );
  }
  return (
    <ApprovalCard approval={approval} deciding={deciding} onDecide={onDecide} />
  );
};
